import React, { ReactElement } from "react";
import styled from "styled-components";

import { trackEvent } from "@trailhead/analytics";
import {
  breakpoints,
  Button as AsurionButton,
  ButtonVariant,
  Text,
  Size,
  colors,
} from "@trailhead/asurion-ui";
import {
  ExpertSupportButtonType,
  hasMissingEmbeddedContent,
} from "@trailhead/contentful";
import { FlexColumnContainer, useWindowDimensions } from "@trailhead/core-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { LottiePlayer } from "@trailhead/lottie";

import { useChatClient } from "./useChatClient";
import { useIsChatEnabled } from "./useIsChatEnabled";
import { ExpertSupportProps } from "./propTypes";

const QuestionsContainer = styled(FlexColumnContainer)`
  width: 100%;
  border-radius: 0.5rem;
  background-color: ${colors.neutralBrightest};
  align-items: center;
`;

const MarginContainer = styled(FlexColumnContainer)`
  align-items: center;
  width: 100%;
`;

const CallToActionContainer = styled(FlexColumnContainer)<{
  offsetTopSection: boolean | undefined;
} >`
  gap: 1.5rem;
  padding: 2rem 1rem 2rem 1rem;
  z-index: 1;

  ${({ offsetTopSection }) =>
    offsetTopSection
      ? `  
      position: absolute;

      ${breakpoints.lg} {
        top: -1.5rem;
      }`
      : ""}
`;

const CallToActionHeadersContainer = styled(FlexColumnContainer)`
  gap: 0.8125rem;
  align-items: center;
`;

const CallToActionHeader = styled(Text)`
  display: block;
  text-align: center;
  line-height: 130%;
  ${breakpoints.lg} {
    max-width: 19.0625rem;
  }
`;

const CallToActionSubHeader = styled(Text)`
  display: block;
  text-align: center;
  line-height: 130%;
  max-width: 21.625rem;
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  align-self: center;
  line-height: none;
  max-height: 2rem;
`;

const CallToActionButtonsContainer = styled(FlexColumnContainer)`
  gap: 0.75rem;
`;

const AnimationContainer = styled(FlexColumnContainer)`
  justify-content: end;
  align-self: center;

  ${breakpoints.xs} {
    padding-top: 1.875rem;
  }

  ${breakpoints.sm} {
    padding-top: 1.5rem;
  }

  ${breakpoints.md} {
    padding-top: unset;
  }
`;

const DEFAULT_SUBTEXT = "We're here for you if you need us.";

//TODO: Will be changed to ExpertSupportCTA once it gets deployed
export const ExpertSupportCTAV2: React.FC<ExpertSupportProps> = ({
  content = {
    title: "Talk to an expert",
    subtext: DEFAULT_SUBTEXT,
    primaryButtonText: "Call an expert",
    primaryButtonType: ExpertSupportButtonType.CALL,
    secondaryButtonText: "Chat with us",
    secondaryButtonType: ExpertSupportButtonType.CHAT,
    image: undefined,
    supportNumber: undefined,
    lottie: {
      autoplay: true,
      loop: 0,
      animationData: undefined,
      altLabel: "expert support animation"
    },
  },
  extraProps = {
    offsetTopSection: false,
    headerProps: undefined,
    lottieStyleProps: "",
  },
}) => {
  const t = useSanitizedTranslation();
  const ChatClient = useChatClient();
  const openChat = (): void => {
    trackEvent("ExpertSupportCTA_Chat_Click");
    ChatClient.openMessagingOverlay();
  };
  const enableMessaging = useIsChatEnabled();

  const openCall = (): void => {
    if (content.supportNumber) {
      window.open(`tel: ${content.supportNumber}`, "_self");
      trackEvent("ExpertSupportCTA_Call_Click", {
        buttonText: content.primaryButtonText,
      });
    }
  };

  const renderSubtext = (): string => {
    const subtext = content.subtext ? content.subtext : "";
    return hasMissingEmbeddedContent(subtext) ? DEFAULT_SUBTEXT : subtext;
  };

  const { width } = useWindowDimensions();
  const isDesktopWide = width >= Size.XXL;

  const {
    offsetTopSection,
    headerProps,
    lottieStyleProps,
  }: ExpertSupportProps["extraProps"] = extraProps;

  const renderButtonWithType = (
    buttonText: string,
    buttonType: ExpertSupportButtonType,
    buttonVariant: ButtonVariant,
  ): ReactElement | null => {
    switch (buttonType) {
      case ExpertSupportButtonType.CALL:
        return content.supportNumber ? (
          <AsurionButtonWrapper
            iconSrc="Phone"
            size="medium"
            variant={buttonVariant}
            onClick={openCall}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t(buttonText),
              }}
            />
          </AsurionButtonWrapper>
        ) : null;
      case ExpertSupportButtonType.CHAT:
        return enableMessaging ? (
          <AsurionButtonWrapper
            iconSrc="ChatBubbleCircle"
            size="medium"
            variant={buttonVariant}
            onClick={openChat}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t(buttonText),
              }}
            />
          </AsurionButtonWrapper>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <MarginContainer>
      <QuestionsContainer>
        <CallToActionContainer offsetTopSection={offsetTopSection}>
          <CallToActionHeadersContainer>
            <CallToActionHeader
              size={4}
              {...headerProps}
              dangerouslySetInnerHTML={{
                __html: t(content.title),
              }}
            />
            <CallToActionSubHeader
              size={3}
              color={colors.neutralDeepest}
              dangerouslySetInnerHTML={{
                __html: t(renderSubtext()),
              }}
            />
          </CallToActionHeadersContainer>
          <CallToActionButtonsContainer>
            {content.primaryButtonText && content.primaryButtonType
              ? renderButtonWithType(
                  content.primaryButtonText,
                  content.primaryButtonType,
                  "default",
                )
              : null}
            {content.secondaryButtonText && content.secondaryButtonType
              ? renderButtonWithType(
                  content.secondaryButtonText,
                  content.secondaryButtonType,
                  isDesktopWide ? "text" : "flat",
                )
              : null}
          </CallToActionButtonsContainer>
        </CallToActionContainer>
        {content.lottie?.animationData ? (
          <AnimationContainer>
            <LottiePlayer {...content.lottie} style={lottieStyleProps} />
          </AnimationContainer>
        ) : null}
      </QuestionsContainer>
    </MarginContainer>
  );
};
